import type {
	ActiveInlineCommentsQueryType,
	CommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	CommentsDataMap,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { ActiveInlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.graphql.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

export const refreshCommentThread = (contentId: string, markerRef: string): Promise<any> =>
	queryInterop<ActiveInlineCommentsQueryType>({
		query: ActiveInlineCommentsQuery,
		fetchPolicy: 'network-only',
		variables: {
			pageId: contentId,
			inlineMarkerRefList: [markerRef],
			contentStatus: ['DRAFT', 'CURRENT'],
		},
	});

export const fetchCommentThread = async (
	contentId: string,
	markerRef: string,
): Promise<ActiveInlineCommentsQueryType> => {
	const commentThreadDataResult = await queryInterop<ActiveInlineCommentsQueryType>({
		query: ActiveInlineCommentsQuery,
		fetchPolicy: 'network-only',
		variables: {
			pageId: contentId,
			inlineMarkerRefList: [markerRef],
			contentStatus: ['DRAFT', 'CURRENT'],
		},
	});
	return commentThreadDataResult.data;
};

export const handleAddNewCommentThread = async ({
	contentId,
	markerRef,
	addNewCommentThreads,
}: {
	contentId: string | undefined;
	markerRef: string;
	addNewCommentThreads: ((newCommentThreads: CommentsDataMap) => void) | undefined;
}): Promise<void> => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	if (comment) {
		addNewCommentThreads &&
			addNewCommentThreads({
				inline: {
					[markerRef]: {
						...comment,
						isOpen: true,
						wasRemovedByAnotherUser: false,
						type: CommentType.INLINE,
					} as CommentData,
				},
				general: {},
			});
	}
};

export const handleAddedReplyInCommentThread = async ({
	contentId,
	markerRef,
	commentId,
	addReplyToCommentThread,
	commentType,
	storedCommentData,
	setStoredCommentData,
}: {
	contentId: string | undefined;
	markerRef: string;
	commentId: string;
	addReplyToCommentThread:
		| ((parentMarkerRef: string, reply: ReplyData, commentType: CommentType) => void)
		| undefined;
	commentType: CommentType;
	storedCommentData: CommentData | null;
	setStoredCommentData?: (commentData: any) => void;
}): Promise<void> => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	const replies = comment?.replies;
	const reply = replies?.filter((r) => r?.id === commentId)[0];
	if (reply) {
		// Update storedCommentData if it exists by adding the new reply
		if (storedCommentData && setStoredCommentData) {
			const updatedCommentData = {
				...storedCommentData,
				replies: [...(storedCommentData.replies || []), reply],
			};

			// Only update if inlineMarkerRef matches between stored and fetched comment
			if (
				(storedCommentData.location as CommentLocation)?.inlineMarkerRef ===
				(comment?.location as CommentLocation)?.inlineMarkerRef
			) {
				setStoredCommentData(updatedCommentData);
			}
		}

		addReplyToCommentThread &&
			addReplyToCommentThread(
				markerRef,
				{
					...reply,
				} as ReplyData,
				commentType,
			);
	}
};

export const handleEditedComment = async ({
	contentId,
	markerRef,
	commentId,
	updateCommentBody,
}: {
	contentId: string | undefined;
	markerRef: string;
	commentId: string;
	updateCommentBody: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
		displayName?: string | null,
	) => void;
}): Promise<void> => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];

	const targetComment =
		comment?.id === commentId ? comment : comment?.replies?.find((r) => r?.id === commentId);

	if (targetComment && updateCommentBody) {
		updateCommentBody(
			commentId,
			markerRef,
			targetComment.body.value,
			CommentType.INLINE,
			targetComment.version.by?.displayName,
		);
	}
};
